import * as pc from "playcanvas";

import type { Number4 } from "../types";
import { isApproximately } from "./Number";

export interface ReadonlyVector4 {
  get x(): number;
  get y(): number;
  get z(): number;
  get w(): number;

  clone(): Vector4;
  dot(rhs: ReadonlyVector4): number;
  equals(rhs: ReadonlyVector4): boolean;
  equalsApprox(rhs: ReadonlyVector4, epsilon?: number): boolean;
  length(): number;
  lengthSq(): number;
  toString(): string;
  toArray(): Number4;

  // Extensions
  distance(rhs: ReadonlyVector4): number;
  distanceSq(rhs: ReadonlyVector4): number;
  isApproximately(rhs: ReadonlyVector4): boolean;
  isFinite(): boolean;

  // Convert for use in external JS code.
  // This is unsafe and should only be used when necessary.
  unsafeCast(): Vector4;
}

declare module "playcanvas" {
  interface Vec4 extends ReadonlyVector4 {
    x: number;
    y: number;
    z: number;
    w: number;

    add(rhs: ReadonlyVector4): this;
    add2(lhs: ReadonlyVector4, rhs: ReadonlyVector4): this;
    addScalar(scalar: number): this;
    addScaled(rhs: ReadonlyVector4, scalar: number): this;
    ceil(src?: ReadonlyVector4): this;
    clone(): Vector4;
    copy(rhs: ReadonlyVector4): this;
    div(rhs: ReadonlyVector4): this;
    div2(lhs: ReadonlyVector4, rhs: ReadonlyVector4): this;
    divScalar(scalar: number): this;
    dot(rhs: ReadonlyVector4): number;
    equals(rhs: ReadonlyVector4): boolean;
    equalsApprox(rhs: ReadonlyVector4, epsilon?: number): boolean;
    floor(src?: ReadonlyVector4): this;
    length(): number;
    lengthSq(): number;
    lerp(lhs: ReadonlyVector4, rhs: ReadonlyVector4, alpha: number): this;
    max(rhs: ReadonlyVector4): this;
    min(rhs: ReadonlyVector4): this;
    mul(rhs: ReadonlyVector4): this;
    mul2(lhs: ReadonlyVector4, rhs: ReadonlyVector4): this;
    mulScalar(scalar: number): this;
    normalize(src?: ReadonlyVector4): this;
    round(src?: ReadonlyVector4): this;
    set(x: number, y: number, z: number, w: number): this;
    sub(rhs: ReadonlyVector4): this;
    sub2(lhs: ReadonlyVector4, rhs: ReadonlyVector4): this;
    subScalar(scalar: number): this;
    toString(): string;
    toArray(): Number4;
    copyToArray(array: Array<number> | Float32Array | Float64Array, offset?: number): this;

    // Extensions
    fromArray(array: Readonly<number[]>, offset?: number): this;
    distance(rhs: ReadonlyVector4): number;
    distanceSq(rhs: ReadonlyVector4): number;
    isApproximately(rhs: ReadonlyVector4): boolean;
    isFinite(): boolean;
    project(rhs: ReadonlyVector4): this;
    recycle(): void;
  }
}

pc.Vec4.prototype.isFinite = function () {
  return Number.isFinite(this.x) && Number.isFinite(this.y) && Number.isFinite(this.z);
};

pc.Vec4.prototype.isApproximately = function (rhs) {
  return isApproximately(this.x, rhs.x) && isApproximately(this.y, rhs.y) && isApproximately(this.z, rhs.z);
};

pc.Vec4.prototype.recycle = function () {
  pool.push(this);
};

pc.Vec4.prototype.fromArray = function (array: number[], offset = 0) {
  this.x = array[offset];
  this.y = array[offset + 1];
  this.z = array[offset + 2];
  this.w = array[offset + 3];
  return this;
};

pc.Vec4.prototype.distance = function (rhs: ReadonlyVector4) {
  return Math.sqrt(this.distanceSq(rhs));
};

pc.Vec4.prototype.distanceSq = function (rhs: ReadonlyVector4) {
  const x = this.x - rhs.x;
  const y = this.y - rhs.y;
  const z = this.z - rhs.z;
  return x * x + y * y + z * z;
};

pc.Vec4.prototype.project = function (rhs: ReadonlyVector4) {
  const a_dot_b = this.x * rhs.x + this.y * rhs.y + this.z * rhs.z + this.w * rhs.w;
  const b_dot_b = rhs.x * rhs.x + rhs.y * rhs.y + rhs.z * rhs.z + rhs.w * rhs.w;
  const s = a_dot_b / b_dot_b;
  this.x = rhs.x * s;
  this.y = rhs.y * s;
  this.z = rhs.z * s;
  this.w = rhs.w * s;
  return this;
};

pc.Vec4.prototype.unsafeCast = function () {
  return this;
};

const pool: Vector4[] = [];

// @ts-expect-error - Override static constants with ReadonlyVector3 type (intentionally incompatible with base class)
export class Vector4 extends pc.Vec4 {
  static isInstance(value: unknown) {
    return value instanceof pc.Vec4;
  }

  static override ZERO = Vector4.makeReadonly(0, 0, 0, 0);
  static override HALF = Vector4.makeReadonly(0.5, 0.5, 0.5, 0.5);
  static override ONE = Vector4.makeReadonly(1, 1, 1, 1);

  static fromArray(array: Readonly<Number4>) {
    return this.make(array[0], array[1], array[2], array[3]);
  }

  /**
   * @summary Creates a vector from a variety of input types.
   */
  static from(json: unknown): Vector4 {
    if (Vector4.isInstance(json)) {
      return json.clone();
    } else if (typeof json === "number") {
      return this.make(json, json, json, json);
    } else if (
      Array.isArray(json) &&
      json.length >= 4 &&
      typeof json[0] === "number" &&
      typeof json[1] === "number" &&
      typeof json[2] === "number" &&
      typeof json[3] === "number"
    ) {
      return this.make(json[0], json[1], json[2], json[3]);
    } else if (
      typeof json === "object" &&
      json !== null &&
      "x" in json &&
      "y" in json &&
      "z" in json &&
      "w" in json &&
      typeof json.x === "number" &&
      typeof json.y === "number" &&
      typeof json.z === "number" &&
      typeof json.w === "number"
    ) {
      return this.make(json.x, json.y, json.z, json.w);
    }

    throw new Error("Invalid input for Vector4.from");
  }

  /**
   * @summary Combines two vectors with a blend factor.
   */
  static lerp(lhs: ReadonlyVector4, rhs: ReadonlyVector4, blend: number): Vector4 {
    return Vector4.make(
      lhs.x + blend * (rhs.x - lhs.x),
      lhs.y + blend * (rhs.y - lhs.y),
      lhs.z + blend * (rhs.z - lhs.z),
      lhs.w + blend * (rhs.w - lhs.w)
    );
  }

  /**
   * @summary Creates a new vector with the given components.
   */
  static make(x = 0, y = 0, z = 0, w = 0): Vector4 {
    return pool.pop()?.set(x, y, z, w) ?? new Vector4(x, y, z, w);
  }

  /**
   * Create a readonly Vector2 that cannot be recycled.
   */
  static makeReadonly(x: number, y: number, z: number, w: number): ReadonlyVector4 {
    return this.make(x, y, z, w);
  }

  /**
   * @summary Creates a new uninitialized vector.
   *          This will return a recycled vector if available, or a new one if not.
   *          The recycled vector will still have its old values
   */
  static makeUninitialized(): Vector4 {
    return pool.pop() || new Vector4();
  }
}
